import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LayoutService } from '../../../../common/services/layout.service';
import { RelativeTimePipe } from '../../../../common/pipes/relative-time.pipe';
import { Notification } from '../../entities/notification.entity';
import { NotificationService } from '../../services/notification.service';

const NOTIFICATION_TYPE_KEYS: Record<string, string> = {
  ASSIGNED: 'common.notifications.type.assigned',
  REASSIGNED: 'common.notifications.type.reassigned',
  UNASSIGNED: 'common.notifications.type.unassigned',
  STARTED: 'common.notifications.type.started',
  SUBMITTED: 'common.notifications.type.submitted',
  APPROVED: 'common.notifications.type.approved',
  REJECTED: 'common.notifications.type.rejected',
};

const NOTIFICATION_TYPE_ICONS: Record<string, string> = {
  ASSIGNED: 'pi-user-plus',
  REASSIGNED: 'pi-directions',
  UNASSIGNED: 'pi-user-minus',
  STARTED: 'pi-play',
  SUBMITTED: 'pi-upload',
  APPROVED: 'pi-check-circle',
  REJECTED: 'pi-times-circle',
};

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, RelativeTimePipe],
  templateUrl: './notification-panel.component.html',
})
export class NotificationPanelComponent implements OnInit {
  readonly layout = inject(LayoutService);
  readonly notificationService = inject(NotificationService);

  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  readonly items = this.notificationService.items;
  readonly unreadCount = this.notificationService.unreadCount;
  readonly loading = this.notificationService.loading;
  readonly hasMore = this.notificationService.hasMore;

  ngOnInit(): void {
    this.notificationService.loadInitial();
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this.layout.toggleNotificationPanel();
  }

  loadMore(): void {
    this.notificationService.loadMore();
  }

  markAllRead(): void {
    this.notificationService.markAllRead().subscribe();
  }

  clearAll(): void {
    this.notificationService.clearAll().subscribe();
  }

  onItemClick(notification: Notification): void {
    if (!notification.read) {
      this.notificationService.markRead(notification.uuid).subscribe();
    }
    this.layout.closeNotificationPanel();
    if (notification.analysisUuid) {
      // Si la notificación es de una medida correctiva, el detalle del análisis la enfoca.
      this.router.navigate(['/analysis/details', notification.analysisUuid], {
        queryParams: notification.requirementUuid ? { measure: notification.requirementUuid } : undefined,
      });
    }
  }

  clearOne(event: Event, uuid: string): void {
    event.stopPropagation();
    this.notificationService.clear(uuid).subscribe();
  }

  badgeLabel(count: number): string {
    return count > 99 ? '99+' : String(count);
  }

  typeIcon(type: string): string {
    return NOTIFICATION_TYPE_ICONS[type] ?? 'pi-bell';
  }

  message(notification: Notification): string {
    const key = NOTIFICATION_TYPE_KEYS[notification.type];
    if (!key) return notification.fallbackMessage;

    const translated = this.transloco.translate(key, notification.params);
    return translated !== key ? translated : notification.fallbackMessage;
  }

  @HostListener('document:click')
  closePanel(): void {
    this.layout.closeNotificationPanel();
  }
}
